import { useEffect, useMemo, useRef } from 'react';
import type { Marker as MarkerType } from 'leaflet';
import { divIcon } from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import { Text } from '@querodelivery/uni-react';
import { FOCUSED_PIN_Z_INDEX } from '@/_components/map/map-pin/constants';
import { ROUTE_MANAGER_ACTION_CAPTION } from '../../constants';
import type { MapPosition } from '../../types';
import styles from './route-action-pin.module.scss';

interface RouteActionPinParams {
  index: number;
  position: MapPosition;
  label: string;
  time?: string;
  isFocused: boolean;
  executedByManager?: boolean;
  onClick?: () => void;
}

export const RouteActionPin = ({
  index,
  position,
  label,
  time,
  isFocused,
  executedByManager = false,
  onClick,
}: RouteActionPinParams) => {
  const markerRef = useRef<MarkerType>(null);
  const icon = useMemo(
    () =>
      divIcon({
        className: styles.wrapper,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
        popupAnchor: [0, -12],
        html: `<div class="${styles.pin}${
          executedByManager ? ` ${styles.executedByManager}` : ''
        }">${index}</div>`,
      }),
    [executedByManager, index],
  );

  useEffect(() => {
    if (!isFocused) {
      markerRef.current?.closePopup();
      return;
    }

    markerRef.current?.openPopup();
  }, [isFocused]);

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={icon}
      zIndexOffset={isFocused ? FOCUSED_PIN_Z_INDEX : 50}
      eventHandlers={{
        mouseover: () => markerRef.current?.openPopup(),
        mouseout: () => {
          if (!isFocused) {
            markerRef.current?.closePopup();
          }
        },
        click: () => onClick?.(),
      }}
    >
      <Popup className={styles.popup} closeButton={false} autoPan={false}>
        <div className={styles.content}>
          <Text as="span" fontSize="xs" fontWeight="semibold" tone="highlight">
            {label}
          </Text>
          {executedByManager && (
            <Text
              as="span"
              className={styles.managerCaption}
              fontSize="xxs"
              fontWeight="semibold"
            >
              {ROUTE_MANAGER_ACTION_CAPTION}
            </Text>
          )}
          {time && (
            <Text as="span" fontSize="xxs" tone="supporting">
              {time}
            </Text>
          )}
        </div>
      </Popup>
    </Marker>
  );
};
